/**
 * The evidence outbox.
 *
 * ADR 0005: the decision never waits for the bytes. The decision is written
 * first, on the server, and the photos then queue here in IndexedDB on the
 * phone and retry on their own. Nothing in that flow can lose a decision --
 * a decision with a missing photo is a repairable gap, and a decision lost to a
 * dropped connection is not repairable at all.
 *
 * The queue is deliberately dumb: no locking, no conflict states, exponential
 * backoff with jitter, and a retry that is safe to repeat because
 * storeContent is idempotent.
 */

const DB_NAME = "gpl-outbox";
const DB_VERSION = 1;
const STORE = "pending";

export type OutboxItem = {
  evidenceId: number;
  blob: Blob;
  sha256: string;
  contentType: string;
  attempts: number;
  nextAt: number;
  lastError: string | null;
  /** Which decision it belongs to, so the UI can say what is still missing. */
  decisionId: number;
};

let dbp: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  dbp ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const d = req.result;
      if (!d.objectStoreNames.contains(STORE)) {
        d.createObjectStore(STORE, { keyPath: "evidenceId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbp;
}

/**
 * Run work in one transaction and resolve when the TRANSACTION commits, not when
 * the first request succeeds.
 *
 * Resolving on the request is wrong for anything that derives a second request
 * from the first, and recordFailure is exactly that: it issues a get, and its
 * onsuccess issues the put that actually records the failure. Assigning
 * req.onsuccess here overwrote that same handler, so the put was never issued
 * and the caller was told it had been. A dropped connection therefore left
 * attempts at 0 and lastError null: no backoff, no explanation to the surveyor,
 * and a blob whose decision had been deleted retrying forever behind a badge
 * nobody could clear.
 */
function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (d) =>
      new Promise<T>((resolve, reject) => {
        const t = d.transaction(STORE, mode);
        let request: IDBRequest<T>;
        try {
          request = fn(t.objectStore(STORE));
        } catch (err) {
          reject(err);
          return;
        }
        // Read the result at commit time: by then any derived request has run.
        t.oncomplete = () => resolve(request.result);
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error);
      }),
  );
}

export const enqueue = (item: Omit<OutboxItem, "attempts" | "nextAt" | "lastError">) =>
  tx("readwrite", (s) => s.put({ ...item, attempts: 0, nextAt: 0, lastError: null }));

export const dequeue = (evidenceId: number) => tx("readwrite", (s) => s.delete(evidenceId));

export const listOutbox = (): Promise<OutboxItem[]> => tx("readonly", (s) => s.getAll() as IDBRequest<OutboxItem[]>);

const recordFailure = (evidenceId: number, attempts: number, lastError: string, nextAt: number) =>
  tx("readwrite", (s) => {
    const g = s.get(evidenceId) as IDBRequest<OutboxItem | undefined>;
    g.onsuccess = () => {
      const cur = g.result;
      if (cur) s.put({ ...cur, attempts, lastError, nextAt });
    };
    return g;
  });

/** 5s, 10s, 20s ... capped at 5 minutes, with +/-20% jitter so a tower full of
 *  phones coming back into coverage does not stampede the tunnel. */
function backoffMs(attempts: number): number {
  const base = Math.min(5 * 60_000, 5_000 * 2 ** Math.max(0, attempts - 1));
  return Math.round(base * (0.8 + Math.random() * 0.4));
}

export type FlushEvent = { pending: number; uploading: number; lastError: string | null; sent: number };

/**
 * Whoever starts a flush is not always the component that has to show it.
 * Recording a decision calls flush() directly, so before this the badge only
 * moved on the 15s tick: a surveyor whose upload died saw a stale count for
 * fifteen seconds, and a successful drain sat on screen just as long.
 */
const listeners = new Set<(e: FlushEvent) => void>();
const emit = (e: FlushEvent) => {
  for (const l of listeners) {
    try {
      l(e);
    } catch {
      /* one broken listener must not take a flush down with it */
    }
  }
};

/** Subscribe to every flush, whoever started it. Returns an unsubscribe. */
export const onFlushEvent = (listener: (e: FlushEvent) => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Try every due item once. Never throws: a flush that fails is a flush that
 * will run again, and a rejected promise here would take the UI down with it.
 */
export async function flush(): Promise<{ sent: number; failed: number; remaining: number }> {
  let sent = 0;
  let failed = 0;
  let lastError: string | null = null;

  let items: OutboxItem[];
  try {
    items = await listOutbox();
  } catch (err) {
    emit({ pending: 0, uploading: 0, lastError: String(err), sent: 0 });
    return { sent, failed, remaining: 0 };
  }

  const due = items.filter((i) => i.nextAt <= Date.now());
  emit({ pending: items.length, uploading: due.length, lastError: null, sent: 0 });

  for (const item of due) {
    try {
      const res = await fetch(`/api/evidence/${item.evidenceId}/content`, {
        method: "PUT",
        headers: {
          "content-type": item.contentType || "application/octet-stream",
          "x-content-sha256": item.sha256,
        },
        body: item.blob,
      });
      if (res.ok) {
        await dequeue(item.evidenceId);
        sent += 1;
        continue;
      }
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      // 4xx other than 408/429 will not fix themselves: the row is gone, the
      // bytes are refused, or the checksum will never match. Retrying forever
      // would leave a permanently pending badge nobody can clear.
      const permanent = res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429;
      const attempts = item.attempts + 1;
      if (permanent && attempts >= 2) {
        await dequeue(item.evidenceId);
        failed += 1;
        lastError = body.error ?? `Ditolak server (${res.status}).`;
        continue;
      }
      lastError = body.error ?? `Gagal (${res.status}).`;
      await recordFailure(item.evidenceId, attempts, lastError, Date.now() + backoffMs(attempts));
      failed += 1;
    } catch (err) {
      // Offline, DNS, tunnel down: the common case, and exactly what this queue exists for.
      const attempts = item.attempts + 1;
      lastError = "Koneksi terputus. Akan dicoba lagi otomatis.";
      await recordFailure(item.evidenceId, attempts, lastError, Date.now() + backoffMs(attempts));
      failed += 1;
      break;
    }
  }

  const remaining = (await listOutbox().catch(() => [])).length;
  emit({ pending: remaining, uploading: 0, lastError, sent });
  return { sent, failed, remaining };
}

/** Runs whenever there is a reason to believe the network is usable. */
export function startAutoFlush(): () => void {
  let running = false;
  const tick = async () => {
    if (running) return;
    running = true;
    try {
      await flush();
    } finally {
      running = false;
    }
  };
  void tick();
  const timer = setInterval(tick, 15_000);
  const onOnline = () => void tick();
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void tick();
  });
  return () => {
    clearInterval(timer);
    window.removeEventListener("online", onOnline);
  };
}
